import { DateTime } from "luxon";

// Names for places made from photos (4.10.55, 2026-10-26). A journal place
// nobody identified used to be named after the photo's file —
// "PXL_20261014_130905244.TS-000" — and that name showed in the schedule and
// the itinerary. Now a name that looks like a camera file name (or the
// date-time name the app gives uploads) becomes "Photo, 14 Oct 13:09"; a
// real name someone gave the photo ("Big Ben") is kept. isFileLikeName also
// finds the places still carrying such a name, for the "rename these" hint.
// Kept identical in andiamo-web and andiamo-places (src/Common).

const IMAGE_EXT = /\.(jpe?g|heic|heif|png|webp|gif|tiff?|dng|mp4|mov)$/i;

const FILE_LIKE = [
  // Camera / phone / app prefixes followed by digits: PXL_2026…, IMG_1234,
  // DSC01234, MVIMG_…, Screenshot_2026…, WhatsApp Image 2026-…
  /^(pxl|img|dsc|dscn|dscf|dcim|mvimg|vid|pano|burst|sam|gopr|dji|screenshot|screen shot|whatsapp image|photo|image)[ _-]?\d/i,
  // 20261014_130905, 2026-10-14 13.09.05, 2026-10-14_13-09
  /^\d{8}[ _-]\d{4,6}/,
  /^\d{4}-\d{2}-\d{2}[ _T]\d{2}[.:_-]?\d{2}/,
  // The date-time name uploads get in the app's photo list: "Jun 27, 2026, 10:04:12 AM"
  /^[A-Z][a-z]{2} \d{1,2}, \d{4},? \d{1,2}:\d{2}/,
  // Long hex/uuid-style ids
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}/i,
  // Anything still ending in an image extension, or with ".TS-000"-style tails
  /\.(ts|mp)-?\d+$/i,
];

export function stripImageExtension(name) {
  return String(name || "").replace(IMAGE_EXT, "").trim();
}

export function isFileLikeName(name) {
  const raw = String(name || "").trim();
  if (!raw) return false;
  if (IMAGE_EXT.test(raw)) return true;
  const n = stripImageExtension(raw);
  return FILE_LIKE.some((re) => re.test(n));
}

// "Photo, 14 Oct 13:09" from an ISO date-time; "Photo" without one.
export function photoDateName(isoDate) {
  const d = isoDate ? DateTime.fromISO(isoDate) : null;
  return d && d.isValid ? `Photo, ${d.toFormat("d LLL HH:mm")}` : "Photo";
}

// The default name for a place made from this photo, when nothing better
// (an identified landmark, a typed name, the city) is known.
export function photoDefaultName(photo) {
  const n = stripImageExtension(photo?.name || "");
  if (n && !isFileLikeName(n)) return n;
  return photoDateName(photo?.exif_data?.date);
}

// Places (not hidden) still named after a photo file — for the hint in the
// Places list and the note after saving.
export function leftoverFileNamePlaces(guide) {
  return (guide?.places?.placesList || []).filter((p) => !p.Hidden && isFileLikeName(p.Name));
}

// " 2 places still have photo file names (PXL_…, IMG_…) — rename them in
// Places." — or "" when there are none. Added to the "saved in cloud" message.
export function leftoverNamesNote(guide) {
  const left = leftoverFileNamePlaces(guide);
  if (left.length === 0) return "";
  const shown = left.slice(0, 3).map((p) => p.Name).join(", ") + (left.length > 3 ? ", …" : "");
  return ` ${left.length === 1 ? "1 place still has a photo file name" : `${left.length} places still have photo file names`} (${shown}) — rename them in Places.`;
}

// The name a place made from a photo cluster gets, in order (4.10.55):
// a name the person typed in Review Places (they recognise the place — also
// how a landmark too far away to be suggested gets named) > a landmark they
// picked from Identify > the typed base name ("Rome_3") > the city Identify
// found > the photo's own name or "Photo, 14 Oct 13:09". The caller still
// makes it unique (Common.uniquePlaceName).
export function clusterBaseName({ typed, picked, preview, city, photo }) {
  const t = String(typed || "").trim();
  if (t) return t;
  if (picked) return picked;
  if (preview) return preview;
  if (city) return city;
  return photoDefaultName(photo);
}

// The search result to pre-select (2026-11-02, as mobile): the one whose title is the place's current name (case
// and accents ignored), so "None of these" isn't chosen over an exact match. null when none matches.
export function matchingCandidate(name, candidates) {
  const n = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
  const want = n(name);
  if (!want) return null;
  const hit = (candidates || []).find((c) => n(c.title) === want);
  return hit ? hit.title : null;
}
